import { useEffect, useRef, useState } from "react";
import {
  InlineFieldGroup,
  type InlineGroupField,
} from "@/components/inline-field-group";

export type SupplierRecipientEnvelope = {
  email: string | null;
  name: string | null;
  cc_emails: string[];
  source: string;
};

export type SupplierRecipientMutationCallbacks = {
  onSuccess: () => void;
  onError: (error: unknown) => void;
};

const EMAIL_LOCAL_PART = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+$/;
const EMAIL_DOMAIN_LABEL = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/;

function isSupplierRecipientEmail(value: string | null | undefined) {
  const normalized = value?.trim() ?? "";
  if (!normalized || normalized.split("@").length !== 2) return false;
  const [localPart = "", rawDomain = ""] = normalized.split("@");
  const domain = rawDomain.endsWith(".") ? rawDomain.slice(0, -1) : rawDomain;
  if (
    !localPart ||
    localPart.length > 64 ||
    localPart.startsWith(".") ||
    localPart.endsWith(".") ||
    localPart.includes("..") ||
    !EMAIL_LOCAL_PART.test(localPart)
  ) {
    return false;
  }
  const labels = domain.split(".");
  return (
    domain.length <= 253 &&
    labels.length >= 2 &&
    labels.every((label) => EMAIL_DOMAIN_LABEL.test(label))
  );
}

function sourceLabel(source: string) {
  const normalized = source.trim().toLowerCase();
  if (
    normalized.includes("override") ||
    ["operator", "operations", "reviewer"].includes(normalized)
  ) {
    return "PO override";
  }
  if (!normalized || normalized === "missing") {
    return "Missing recipient";
  }
  return "Manufacturer default";
}

type RecipientKey = "to" | "cc";

const RECIPIENT_FIELDS: ReadonlyArray<InlineGroupField<RecipientKey>> = [
  { key: "to", label: "TO email", required: true, autoComplete: "email" },
  { key: "cc", label: "CC emails" },
];

function normalizeCCEmails(value: string, toEmail: string): string[] {
  const seen = new Set<string>();
  const normalizedTo = toEmail.toLowerCase();
  return value
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean)
    .filter((email) => {
      const key = email.toLowerCase();
      if (key === normalizedTo || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function SupplierRecipientEnvelopeEditor({
  id,
  title,
  envelope,
  disabled = false,
  pending = false,
  onEditingChange,
  onSave,
}: {
  id: string;
  title: string;
  envelope: SupplierRecipientEnvelope;
  disabled?: boolean;
  pending?: boolean;
  onEditingChange?: (editing: boolean) => void;
  onSave: (
    envelope: SupplierRecipientEnvelope,
    callbacks: SupplierRecipientMutationCallbacks,
  ) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toError, setToError] = useState("");
  const [ccError, setCCError] = useState("");
  const [serverError, setServerError] = useState("");
  const sectionRef = useRef<HTMLElement>(null);
  const restoreEditFocus = useRef(false);
  const editingRef = useRef(false);
  const onEditingChangeRef = useRef(onEditingChange);
  const source = sourceLabel(envelope.source);

  useEffect(() => {
    onEditingChangeRef.current = onEditingChange;
  }, [onEditingChange]);

  useEffect(
    () => () => {
      if (editingRef.current) onEditingChangeRef.current?.(false);
    },
    [],
  );

  useEffect(() => {
    if (!editing && restoreEditFocus.current && !pending) {
      restoreEditFocus.current = false;
      sectionRef.current
        ?.querySelector<HTMLButtonElement>('button[aria-label="Edit recipients"]')
        ?.focus();
    }
  }, [editing, pending]);

  const clearErrors = () => {
    setToError("");
    setCCError("");
    setServerError("");
  };

  const changeEditing = (next: boolean) => {
    clearErrors();
    if (!next) {
      setSaving(false);
      restoreEditFocus.current = true;
    }
    editingRef.current = next;
    setEditing(next);
    onEditingChange?.(next);
  };

  const submit = (draft: Record<RecipientKey, string>) => {
    const normalizedTo = draft.to.trim();
    const normalizedCC = normalizeCCEmails(draft.cc, normalizedTo);
    const invalidCC = normalizedCC.filter(
      (email) => !isSupplierRecipientEmail(email),
    );
    const nextToError = isSupplierRecipientEmail(normalizedTo)
      ? ""
      : "Enter a valid TO email address.";
    const nextCCError = invalidCC.length
      ? `Fix invalid CC ${invalidCC.length === 1 ? "address" : "addresses"}: ${invalidCC.join(", ")}`
      : "";
    setToError(nextToError);
    setCCError(nextCCError);
    setServerError("");
    if (nextToError || nextCCError) return;

    setSaving(true);
    onSave(
      {
        email: normalizedTo,
        name:
          normalizedTo.toLowerCase() === (envelope.email ?? "").toLowerCase()
            ? envelope.name
            : null,
        cc_emails: normalizedCC,
        source: "operator",
      },
      {
        onSuccess: () => {
          setSaving(false);
          changeEditing(false);
        },
        onError: (error) => {
          setSaving(false);
          setServerError(
            error instanceof Error && error.message
              ? error.message
              : "Recipients were not saved. Review the addresses and try again.",
          );
        },
      },
    );
  };

  const errors = [toError, ccError, serverError].filter(Boolean);

  return (
    <section
      ref={sectionRef}
      aria-labelledby={`${id}-title`}
      className="min-w-0 p-4"
    >
      <div className="min-w-0">
        <h4 id={`${id}-title`} className="text-sm font-semibold">
          {title}
        </h4>
        <p className="text-xs text-muted-foreground">{source}</p>
      </div>

      {/* Typing clears the last save's errors. */}
      <div
        className="mt-3"
        onChange={() => {
          if (errors.length) clearErrors();
        }}
      >
        <InlineFieldGroup
          label="recipients"
          fields={RECIPIENT_FIELDS}
          values={{
            to: envelope.email ?? "",
            cc: envelope.cc_emails.join(", "),
          }}
          editing={editing}
          onEditingChange={changeEditing}
          disabled={disabled || pending}
          pending={saving || pending}
          saveLabel="Save recipients"
          notice={
            errors.length ? (
              <div className="space-y-1">
                {errors.map((error) => (
                  <p
                    key={error}
                    role="alert"
                    className="break-words text-destructive"
                  >
                    {error}
                  </p>
                ))}
              </div>
            ) : null
          }
          onSave={submit}
        >
          <span className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm">
            <span className="text-xs font-medium text-muted-foreground">To</span>
            <span className="min-w-0">
              {envelope.name ? (
                <span className="block break-words font-medium">
                  {envelope.name}
                </span>
              ) : null}
              <span
                className={`block break-all ${envelope.name ? "text-muted-foreground" : ""}`}
              >
                {envelope.email || "Missing email"}
              </span>
            </span>
            <span className="text-xs font-medium text-muted-foreground">CC</span>
            <span className="min-w-0 break-all text-muted-foreground">
              {envelope.cc_emails.length ? envelope.cc_emails.join(", ") : "None"}
            </span>
          </span>
        </InlineFieldGroup>
      </div>
    </section>
  );
}

SupplierRecipientEnvelopeEditor.isValidEmail = isSupplierRecipientEmail;
